import { useState } from "react";
import PublicShell, { Block, SampleNote } from "@/components/PublicShell";
import Waveform from "@/components/Waveform";
import VoiceStudio from "@/components/VoiceStudio";
import useSeo from "@/hooks/useSeo";

const LANGS = [
  { id: "en", label: "English", greeting: "Hello! Welcome to Savy Kerno. I'm your AI demo assistant. How can I help you today?" },
  { id: "hi", label: "हिन्दी", greeting: "नमस्ते! Savy Kerno में आपका स्वागत है। मैं आपकी AI डेमो सहायक हूँ। आज मैं आपकी कैसे मदद कर सकती हूँ?" },
  { id: "te", label: "తెలుగు", greeting: "హలో! Savy Kernoకి స్వాగతం. నేను మీ AI డెమో సహాయకురాలిని. ఈరోజు నేను మీకు ఎలా సహాయపడగలను?" },
];

const SCRIPT = [
  ["Caller", "Hi, do you have any appointments this Friday afternoon?"],
  ["Agent", "Let me check… Friday has 2:30 pm and 4:00 pm free with Priya. Which suits you?"],
  ["Caller", "4:00 pm please."],
  ["Agent", "Done — you're booked for Friday at 4:00 pm. A confirmation will follow shortly."],
];

export default function AiVoiceAgentsPage() {
  useSeo("AI Voice Agents — Savy Kerno", "Savy Kerno AI voice agents greet callers, answer questions and handle appointment requests in English, Hindi or Telugu. Try the free browser voice demo.");
  const [lang, setLang] = useState("en");
  const [step, setStep] = useState(0);
  return (
    <PublicShell
      testid="voice-page"
      eyebrow="AI Voice Agents"
      title="AI answering for your calls, in your voice"
      sub="Create an agent with your greeting, your FAQs and your tone. It can answer questions, qualify enquiries and book appointments straight into your calendar."
    >
      <section className="border-b border-neutral-100 py-12 sm:py-16" data-testid="voice-studio-section">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Savy Kerno Voice Studio</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-500">
            Experience how Savy Kerno can greet customers, answer questions, qualify leads and help
            arrange appointments — directly in your browser. Pick a scenario and language, allow
            microphone access, and just talk.
          </p>
          <div className="mt-6">
            <VoiceStudio />
          </div>
        </div>
      </section>

      <Block title="Configure once, answer always" testid="voice-config">
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            ["Greeting & instructions", "Write how your agent introduces itself and what it may promise."],
            ["Language & voice", "English, Hindi and Telugu conversations in the browser demo today."],
            ["Appointment handling", "Offers only real free slots from your calendar — never invented times."],
            ["Escalation", "Callers who need a person are logged and flagged for your team."],
          ].map(([t, d]) => (
            <div key={t} className="rounded-2xl border border-neutral-200 p-5">
              <p className="text-sm font-bold">{t}</p>
              <p className="mt-1.5 text-xs leading-relaxed text-neutral-500">{d}</p>
            </div>
          ))}
        </div>
      </Block>

      <Block title="Hear the difference a language makes" testid="voice-languages">
        <div className="rounded-2xl border border-neutral-200 bg-neutral-50/70 p-6">
          <p className="text-sm font-bold">Sample greeting<SampleNote /></p>
          <div className="mt-4 flex gap-2" role="tablist" aria-label="Greeting language">
            {LANGS.map((l) => (
              <button
                key={l.id}
                role="tab"
                aria-selected={lang === l.id}
                data-testid={`voice-lang-${l.id}`}
                onClick={() => setLang(l.id)}
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${lang === l.id ? "bg-[#111111] text-white" : "border border-neutral-300 text-neutral-500 hover:border-neutral-400"}`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <p className="mt-4 rounded-xl bg-white px-4 py-3 text-sm text-neutral-700" data-testid="voice-greeting">
            “{LANGS.find((l) => l.id === lang).greeting}”
          </p>
          <p className="mt-3 text-[11px] text-neutral-400">
            In your account the agent speaks these greetings aloud — this page shows the text version only.
          </p>
        </div>
      </Block>

      <Block title="A booking conversation, step by step" testid="voice-script">
        <div className="rounded-2xl border border-neutral-200 bg-neutral-50/70 p-6">
          <p className="text-sm font-bold">Sample conversation<SampleNote /></p>
          <div className="mt-4 space-y-2" data-testid="voice-script-lines">
            {SCRIPT.slice(0, step + 1).map(([who, line], i) => (
              <p key={i} className={`max-w-md rounded-xl px-4 py-2.5 text-sm ${who === "Agent" ? "ml-auto bg-[#111111] text-white" : "bg-white text-neutral-700"}`}>
                <span className="mr-1.5 text-[10px] font-bold uppercase tracking-wider opacity-60">{who}</span>
                {line}
              </p>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => setStep((s) => Math.min(s + 1, SCRIPT.length - 1))}
              disabled={step >= SCRIPT.length - 1}
              data-testid="voice-script-next"
              className="rounded-full bg-[#111111] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-neutral-800 disabled:opacity-40"
            >
              Next line
            </button>
            <button
              onClick={() => setStep(0)}
              data-testid="voice-script-restart"
              className="rounded-full border border-neutral-300 px-4 py-2 text-xs font-semibold transition-colors hover:bg-neutral-50"
            >
              Restart
            </button>
          </div>
        </div>
      </Block>

      <Block title="Availability, honestly" testid="voice-availability">
        <div className="rounded-2xl border border-neutral-200 p-6">
          <Waveform compact />
          <ul className="mt-4 space-y-2 text-sm text-neutral-600">
            <li>✓ In-browser voice conversations work today after you sign in — no phone line needed.</li>
            <li>✓ Agents book real appointments into your calendar with conflict and past-date protection.</li>
            <li>○ Live telephone answering stays off until an approved telephony provider is connected and verified for your account.</li>
          </ul>
        </div>
      </Block>
    </PublicShell>
  );
}
